/*
 * Billing in the console (BILLING-DESIGN.md §9 Phase 6, §10). No jQuery, no inline scripts:
 * every Billing partial is swapped into a tab pane or a modal, so its handlers live here.
 *
 *   List      -- the Application Detail Billing tab: subscriptions, "Return to Billing"
 *   Domain    -- the Domain Billing area's tabs: overview, subscriptions, plans, providers,
 *                webhook events, settings; reconcile now
 *   Provider  -- a provider account and its credentials (write-only), test connection
 *   Plan      -- a plan and its fits; verify its prices with the provider
 *   Settings  -- the installation's Billing switch and intervals
 *
 * A payment never overrides an operator; releasing hands a held License back, so it asks first.
 */
import { ajaxJson, ajaxModal, ajaxSwap, failToast, summaryOf, type ErrorCallback } from '../core/ajax';
import { escapeHtml, one } from '../core/dom';
import { serialize } from '../core/forms';
import { url as absoluteUrl } from '../core/http';
import { confirm, hideModal } from '../core/modal';
import { showTab } from '../core/tabs';
import { toast } from '../core/toast';
import { isValid, setValidationModelState } from '../core/validation';

const PANE = 'ApplicationBillingTab';
const MODAL = 'BillingModal';
const PROVIDER_FORM = 'BillingProviderForm';
const PLAN_FORM = 'BillingPlanForm';
const SETTINGS_FORM = 'BillingSettingsForm';

/**
 * The Domain tabs, by the name on their tab button: each pane and its URL. Spelled out one by
 * one so the route guard (RouteCollisionTests) can resolve every URL to a Web action.
 */
const TABS: Record<string, { pane: string; url: (domain: string) => string }> = {
    Overview: { pane: 'BillingOverviewTab', url: domain => `Billing/Domain/${domain}/Overview` },
    Subscriptions: { pane: 'BillingSubscriptionsTab', url: domain => `Billing/Domain/${domain}/Subscriptions` },
    Plans: { pane: 'BillingPlansTab', url: domain => `Billing/Domain/${domain}/Plans` },
    Providers: { pane: 'BillingProvidersTab', url: domain => `Billing/Domain/${domain}/Providers` },
    Events: { pane: 'BillingEventsTab', url: domain => `Billing/Domain/${domain}/Events` },
    Settings: { pane: 'BillingSettingsTab', url: domain => `Billing/Domain/${domain}/Settings` },
};

function load(applicationId: number | string, show: string, page: number): Promise<void> {
    const params = new URLSearchParams();
    if (show && show !== 'live') params.set('show', show);
    if (page > 1) params.set('page', String(page));
    const query = params.toString();
    return ajaxSwap({
        container: PANE,
        url: `Billing/Application/${applicationId}${query ? `?${query}` : ''}`,
        alertTitle: 'Failed to load billing',
    });
}

/** What releasing does, by why the License is held. */
function consequence(hold: string): string {
    return hold === 'Deleted'
        ? 'Billing will press a new License from the plan and apply what the subscription has paid for.'
        : 'Billing will take the License\'s present end as its own and extend it to what the subscription has paid for.';
}

/** The transport failure of a write with no alert container: a toast, never silence. */
function errorToast(title: string): ErrorCallback {
    return (_xhr, _status, error) => {
        toast('danger', title, error);
        return true;
    };
}

/** Ask, then return a held License to Billing; <paramref name="after"/> reloads what was showing. */
function release(licenseId: string, hold: string, after: () => void): void {
    void confirm({
        title: 'Return to Billing',
        html: `This License is held because it was <strong>${hold === 'Deleted' ? 'deleted' : 'shortened'}</strong> by an operator.<br/><br/>`
            + `${consequence(hold)}<br/><br/>`
            + 'From then on, payments drive it again.',
        confirmText: 'Return to Billing',
        variant: 'warning',
        onConfirm: () => {
            void ajaxJson({
                url: absoluteUrl(`Billing/License/${licenseId}/Release`),
                method: 'POST',
                errorTitle: 'Failed to return the license to Billing',
                onSuccess: data => {
                    toast(data['released'] === true ? 'success' : 'info',
                        data['released'] === true ? 'License returned to Billing' : 'Nothing was held');
                    after();
                },
                onFailure: failToast('Failed to return the license to Billing'),
                onError: errorToast('Failed to return the license to Billing'),
            });
        },
    });
}

function reapply(licenseId: string, plan: string, after: () => void): void {
    void confirm({
        title: 'Re-apply plan terms',
        html: `This License takes <strong>${escapeHtml(plan)}</strong>'s terms as the plan has them now: its template's features, quotas and coverage.<br/><br/>`
            + 'Its id, status, end date and assignments stay. Use it after pointing the plan at another template: '
            + 'subscriptions already running keep the terms they were pressed with until then.',
        confirmText: 'Re-apply terms',
        variant: 'warning',
        onConfirm: () => {
            void ajaxJson({
                url: absoluteUrl(`Billing/License/${licenseId}/Reapply`),
                method: 'POST',
                errorTitle: 'Failed to re-apply the plan terms',
                onSuccess: () => {
                    toast('success', 'Plan terms re-applied');
                    after();
                },
                onFailure: failToast('Failed to re-apply the plan terms'),
                onError: errorToast('Failed to re-apply the plan terms'),
            });
        },
    });
}

export const List = {
    /** Live, held or all subscriptions, from page 1. */
    Show(applicationId: number | string, show: string): boolean {
        void load(applicationId, show, 1);
        return false;
    },

    /** Another page under the current filter. */
    Page(applicationId: number | string, show: string, page: number): boolean {
        void load(applicationId, show, page);
        return false;
    },

    /** Return a held License to Billing, then reload the tab where it stood. */
    Release(applicationId: number | string, licenseId: string, hold: string): boolean {
        release(licenseId, hold, () => void load(applicationId, 'live', 1));
        return false;
    },

    /** Give a License the plan's current template, then reload the live tab. */
    Reapply(applicationId: number | string, licenseId: string, plan: string): boolean {
        reapply(licenseId, plan, () => void load(applicationId, 'live', 1));
        return false;
    },
};

// ── Domain area ─────────────────────────────────────────────────────────────

function domainId(): string {
    return (one('#BillingDomainId') as HTMLInputElement | null)?.value ?? '';
}

function query(params: Params): string {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
        if (value !== null && value !== undefined && value !== '' && value !== 'null') search.set(key, String(value));
    }
    const text = search.toString();
    return text ? `?${text}` : '';
}

type Params = Record<string, string | number | null | undefined>;

/** The filter and page each tab last showed. */
const lastParams: Record<string, Params> = {};

/** Load one Domain tab into its pane: with <paramref name="params"/>, or as it last stood. */
function loadTab(name: string, params?: Params): Promise<void> {
    const tab = TABS[name];
    if (!tab) return Promise.resolve();
    if (params) lastParams[name] = params;
    params = lastParams[name] ?? {};
    return ajaxSwap({
        container: tab.pane,
        url: absoluteUrl(tab.url(domainId()) + query(params)),
        spinnerInto: true,
        alertTitle: `Failed to load ${name.toLowerCase()}`,
    });
}

/** The name of the open Domain tab. */
function openTab(): string {
    const active = one('#BillingDomainTabs .nav-link.active');
    return active?.getAttribute('data-tab') ?? 'Overview';
}

/** The selected value of a filter select, as a number or null. */
function selectedApp(select: HTMLSelectElement): number | null {
    return select.value ? Number(select.value) : null;
}

/** Swap a form's action icon for a spinner while its request is in flight. */
function working(formId: string, icon: string): (on: boolean) => void {
    const scope = one(`#${formId}`)?.closest('.modal-content') ?? one(`#${formId}`);
    return on => {
        scope?.querySelectorAll(icon).forEach(node => node.classList.toggle('d-none', on));
        scope?.querySelectorAll('.fa-spinner').forEach(node => node.classList.toggle('d-none', !on));
    };
}

export const Domain = {
    /** Reload whichever tab is open. */
    Refresh(): boolean {
        void loadTab(openTab());
        return false;
    },

    /** Switch to a tab by name (the overview's "Settings" link). */
    OpenTab(name: string): boolean {
        const tab = one(`#BillingDomainTabs [data-tab="${name}"]`);
        if (tab) showTab(tab);
        return false;
    },

    Subscriptions(show: string, app: number | null, page: number): boolean {
        void loadTab('Subscriptions', { show, app, page });
        return false;
    },

    SubscriptionsApp(this: HTMLSelectElement, show: string): boolean {
        void loadTab('Subscriptions', { show, app: selectedApp(this), page: 1 });
        return false;
    },

    Events(outcome: string, app: number | null, page: number): boolean {
        void loadTab('Events', { outcome, app, page });
        return false;
    },

    EventsOutcome(this: HTMLSelectElement, app: number | null): boolean {
        void loadTab('Events', { outcome: this.value, app, page: 1 });
        return false;
    },

    EventsApp(this: HTMLSelectElement, outcome: string): boolean {
        void loadTab('Events', { outcome, app: selectedApp(this), page: 1 });
        return false;
    },

    PlansApp(this: HTMLSelectElement): boolean {
        void loadTab('Plans', { app: selectedApp(this) });
        return false;
    },

    Release(licenseId: string, hold: string): boolean {
        release(licenseId, hold, () => void loadTab('Subscriptions'));
        return false;
    },

    Reapply(licenseId: string, plan: string): boolean {
        reapply(licenseId, plan, () => void loadTab('Subscriptions'));
        return false;
    },

    /** Check every open subscription of the application against its provider now. */
    Reconcile(applicationId: number): boolean {
        void ajaxJson({
            url: absoluteUrl(`Billing/Application/${applicationId}/Reconcile`),
            method: 'POST',
            errorTitle: 'Reconcile failed',
            onSuccess: data => {
                const r = data['report'] as { ran?: boolean; message?: string; checked?: number; subscriptionsRepaired?: number;
                    invoicesRepaired?: number; licensesHeld?: number; failed?: number } | undefined;
                toast(r?.ran === false ? 'warning' : 'success', r?.ran === false ? 'Reconcile not queued' : 'Reconcile queued', r?.message ?? '');
            },
            onFailure: failToast('Reconcile failed'),
            onError: errorToast('Reconcile failed'),
        });
        return false;
    },
};

// ── Provider accounts ───────────────────────────────────────────────────────

function providerAppId(): string {
    return (one('#BillingProviderAppId') as HTMLInputElement | null)?.value ?? '';
}

export const Provider = {
    Edit(applicationId: number): boolean {
        void ajaxModal({
            modal: MODAL,
            url: absoluteUrl(`Billing/Application/${applicationId}/Provider`),
            formId: PROVIDER_FORM,
            focusId: 'BillingProviderLabel',
            errorTitle: 'Payment provider',
            errorMessage: 'The provider form failed to load:',
        });
        return false;
    },

    Save(): boolean {
        const form = one(`#${PROVIDER_FORM}`) as HTMLFormElement | null;
        if (!form || !isValid(form)) return false;
        const spin = working(PROVIDER_FORM, '.fa-save');
        spin(true);
        void ajaxJson({
            url: absoluteUrl(`Billing/Application/${providerAppId()}/Provider`),
            method: 'POST',
            data: serialize(form),
            alertInto: 'BillingProviderMessageContainer',
            errorTitle: 'Failed to save the payment provider',
            onSuccess: data => {
                hideModal(MODAL);
                const status = String(data['status'] ?? '');
                toast(status === 'Ready' ? 'success' : 'warning', 'Payment provider saved', status === 'Ready' ? 'Ready to take payments.' : `Status: ${status}`);
                void loadTab('Providers');
            },
            onFailure: data => {
                const modelState = (data as { modelState?: Record<string, string[]> } | null)?.modelState;
                if (modelState) setValidationModelState(modelState, `#${PROVIDER_FORM}`);
                toast('danger', 'Failed to save the payment provider', summaryOf(data));
                return true;
            },
            onComplete: () => spin(false),
        });
        return false;
    },

    ClearCredential(applicationId: number, name: string): boolean {
        void confirm({
            title: 'Remove credential',
            message: `Remove the stored ${name}? Until another is entered, the account cannot be used (unless the Api's configuration supplies one).`,
            confirmText: 'Remove',
            variant: 'danger',
            onConfirm: () => {
                void ajaxJson({
                    url: absoluteUrl(`Billing/Application/${applicationId}/Provider/Credential/${encodeURIComponent(name)}/Clear`),
                    method: 'POST',
                    errorTitle: 'Failed to remove the credential',
                    onSuccess: () => {
                        toast('success', `${name} removed`);
                        Provider.Edit(applicationId);
                        void loadTab('Providers');
                    },
                    onFailure: failToast('Failed to remove the credential'),
                    onError: errorToast('Failed to remove the credential'),
                });
            },
        });
        return false;
    },

    Delete(applicationId: number): boolean {
        void confirm({
            title: 'Delete payment provider',
            message: 'Delete this account? Only one nothing was ever sold through can be deleted; otherwise disable it.',
            confirmText: 'Delete',
            variant: 'danger',
            onConfirm: () => {
                void ajaxJson({
                    url: absoluteUrl(`Billing/Application/${applicationId}/Provider/Delete`),
                    method: 'POST',
                    errorTitle: 'Failed to delete the payment provider',
                    onSuccess: () => {
                        hideModal(MODAL);
                        toast('success', 'Payment provider deleted');
                        void loadTab('Providers');
                    },
                    onFailure: failToast('Failed to delete the payment provider'),
                    onError: errorToast('Failed to delete the payment provider'),
                });
            },
        });
        return false;
    },

    Test(applicationId: number): boolean {
        void ajaxJson({
            url: absoluteUrl(`Billing/Application/${applicationId}/Provider/Test`),
            method: 'POST',
            errorTitle: 'Connection test failed',
            onSuccess: data => toast(data['ok'] === true ? 'success' : 'danger',
                data['ok'] === true ? 'Connection works' : 'Connection failed', String(data['message'] ?? '')),
            onFailure: failToast('Connection test failed'),
            onError: errorToast('Connection test failed'),
        });
        return false;
    },
};

// ── Plans ───────────────────────────────────────────────────────────────────

function planAppId(): string {
    return (one('#BillingPlanAppId') as HTMLInputElement | null)?.value ?? '';
}

export const Plan = {
    /** A new plan for the application chosen beside the button. */
    New(): boolean {
        const app = (one('#BillingNewPlanApp') as HTMLSelectElement | null)?.value;
        if (app) Plan.Edit(Number(app), 0);
        return false;
    },

    Edit(applicationId: number, planId: number): boolean {
        void ajaxModal({
            modal: MODAL,
            url: absoluteUrl(`Billing/Application/${applicationId}/Plan/${planId}`),
            formId: PLAN_FORM,
            focusId: 'BillingPlanName',
            errorTitle: 'Plan',
            errorMessage: 'The plan form failed to load:',
        });
        return false;
    },

    /** Ticks or clears every class in the plan form's pick-list. */
    FitAll(on: boolean): boolean {
        document.querySelectorAll<HTMLInputElement>('#BillingPlanFitClasses input[type=checkbox]').forEach(box => { box.checked = on; });
        return false;
    },

    Save(): boolean {
        const form = one(`#${PLAN_FORM}`) as HTMLFormElement | null;
        if (!form || !isValid(form)) return false;
        const spin = working(PLAN_FORM, '.fa-save');
        spin(true);
        void ajaxJson({
            url: absoluteUrl(`Billing/Application/${planAppId()}/Plan`),
            method: 'POST',
            data: serialize(form),
            alertInto: 'BillingPlanMessageContainer',
            errorTitle: 'Failed to save the plan',
            onSuccess: () => {
                hideModal(MODAL);
                toast('success', 'Plan saved');
                void loadTab('Plans');
            },
            onFailure: data => {
                const modelState = (data as { modelState?: Record<string, string[]> } | null)?.modelState;
                if (modelState) setValidationModelState(modelState, `#${PLAN_FORM}`);
                toast('danger', 'Failed to save the plan', summaryOf(data));
                return true;
            },
            onComplete: () => spin(false),
        });
        return false;
    },

    Delete(applicationId: number, planId: number): boolean {
        void confirm({
            title: 'Delete plan',
            message: 'Delete this plan? It has never been sold; nothing else changes.',
            confirmText: 'Delete',
            variant: 'danger',
            onConfirm: () => {
                void ajaxJson({
                    url: absoluteUrl(`Billing/Application/${applicationId}/Plan/${planId}/Delete`),
                    method: 'POST',
                    errorTitle: 'Failed to delete the plan',
                    onSuccess: () => {
                        hideModal(MODAL);
                        toast('success', 'Plan deleted');
                        void loadTab('Plans');
                    },
                    onFailure: failToast('Failed to delete the plan'),
                    onError: errorToast('Failed to delete the plan'),
                });
            },
        });
        return false;
    },

    /** Ask the provider about each of the plan's prices and say what is wrong, if anything. */
    Verify(applicationId: number, planId: number): boolean {
        void ajaxJson({
            url: absoluteUrl(`Billing/Application/${applicationId}/Plan/${planId}/Verify`),
            method: 'POST',
            errorTitle: 'Price check failed',
            onSuccess: data => {
                const checks = (data['checks'] as { interval: string; priceId: string; problem?: string | null; unitAmount?: number | null; currency?: string | null }[]) ?? [];
                if (checks.length === 0) {
                    toast('info', 'No prices to check', 'The plan names no price.');
                    return;
                }
                const problems = checks.filter(c => c.problem);
                const lines = checks.map(c => c.problem
                    ? `${c.interval}: ${c.problem}`
                    : `${c.interval}: ${c.unitAmount ?? '?'} ${(c.currency ?? '').toUpperCase()} (minor units)`);
                toast(problems.length > 0 ? 'danger' : 'success', problems.length > 0 ? 'Price problems' : 'Prices look right', lines.join(' · '));
            },
            onFailure: failToast('Price check failed'),
            onError: errorToast('Price check failed'),
        });
        return false;
    },
};

// ── Settings ────────────────────────────────────────────────────────────────

/**
 * The settings form's fields the operator changed (each input carries data-original): an
 * untouched field is not posted, so it keeps coming from the Api's configuration.
 */
function changedSettings(form: HTMLFormElement): { name: string; value: string }[] {
    const pairs = serialize(form);
    const changed = pairs.filter(pair => {
        const input = form.querySelector<HTMLInputElement>(`[name="${pair.name}"]`);
        const original = input?.getAttribute('data-original');
        return original === null || original === undefined || original !== pair.value;
    });
    // An unchecked switch posts nothing: send it as off when it was on.
    const enabled = form.querySelector<HTMLInputElement>('input[name="Enabled"]');
    if (enabled && !enabled.disabled && !enabled.checked && enabled.getAttribute('data-original') === 'true') {
        changed.push({ name: 'Enabled', value: 'false' });
    }
    if (enabled && !enabled.disabled && enabled.checked && enabled.getAttribute('data-original') === 'true') {
        return changed.filter(pair => pair.name !== 'Enabled' && pair.name !== 'EnabledEditable');
    }
    if (!changed.some(pair => pair.name === 'Enabled')) {
        return changed.filter(pair => pair.name !== 'EnabledEditable');
    }
    return changed;
}

function saveSettings(data: { name: string; value: string }[], title: string): void {
    const spin = working(SETTINGS_FORM, '.fa-save');
    spin(true);
    void ajaxJson({
        url: absoluteUrl('Billing/Settings'),
        method: 'POST',
        data,
        alertInto: 'BillingSettingsMessageContainer',
        errorTitle: 'Failed to save the settings',
        onSuccess: result => {
            toast('success', title, result['enabled'] === true ? 'Billing is on.' : 'Billing is off.');
            void loadTab('Settings');
        },
        onFailure: failToast('Failed to save the settings'),
        onError: errorToast('Failed to save the settings'),
        onComplete: () => spin(false),
    });
}

export const Settings = {
    Save(): boolean {
        const form = one(`#${SETTINGS_FORM}`) as HTMLFormElement | null;
        if (!form) return false;
        const changed = changedSettings(form);
        if (changed.length === 0) {
            toast('info', 'Nothing changed');
            return false;
        }
        saveSettings(changed, 'Billing settings saved');
        return false;
    },

    /** Forget every console setting: the Api's configuration and the defaults apply again. */
    Reset(): boolean {
        void confirm({
            title: 'Use configuration',
            message: 'Remove every Billing setting made here? The on/off switch and the intervals then come from the Api\'s configuration, or their defaults (Billing off).',
            confirmText: 'Use configuration',
            variant: 'warning',
            onConfirm: () => saveSettings([{ name: 'ResetToConfiguration', value: 'true' }], 'Settings reset to configuration'),
        });
        return false;
    },

};
